import {
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from '@mui/material';

type SelectBoxMultiProps<T extends string> = {
  label: string;
  availableItems: T[];
  items: readonly T[];
  setItems: (items: T[]) => void;
  helperText?: string;
  disabled?: boolean;
  transformLabel?: (label: T) => T;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export const SelectBoxMulti = <T extends string>({
  label,
  helperText,
  availableItems,
  items,
  setItems,
  disabled,
  transformLabel,
}: SelectBoxMultiProps<T>) => {
  const isChecked = availableItems.length === items.length;
  const isIndeterminate =
    availableItems.length > 0 && items.length > 0 && items.length < availableItems.length;

  const onChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;

    if (value.includes('_ALL_') && (isIndeterminate || !isChecked)) {
      setItems(availableItems);
      return;
    }
    if (value.includes('_ALL_') && isChecked) {
      setItems([]);
      return;
    }
    if (typeof value === 'string') {
      // On autofill, we get a stringified value.
      setItems(value.split(',') as T[]);
      return;
    }
    setItems(value as T[]);
  };

  return (
    <FormControl sx={{ m: 1, width: '100%', margin: 0 }}>
      <InputLabel
        data-testid="filter-select-form-label"
        id={`${label.toLowerCase()}-select-label`}
        shrink
      >
        {label}
      </InputLabel>
      <Select
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            },
          },
        }}
        disabled={disabled}
        displayEmpty
        input={<OutlinedInput label={label} />}
        labelId={`${label.toLowerCase()}-select-label`}
        multiple
        notched
        onChange={onChange}
        renderValue={() =>
          !isChecked && !isIndeterminate
            ? 'Nothing selected'
            : isIndeterminate
              ? `${items.length} selected`
              : 'All selected'
        }
        value={items as T[]}
      >
        <MenuItem value="_ALL_">
          <Checkbox checked={isChecked} indeterminate={isIndeterminate} />
          <ListItemText primary={'All'} />
        </MenuItem>
        <Divider variant="middle" />
        {availableItems.map((item) => (
          <MenuItem data-testid={`filter-select-form-menuitem-${item}`} key={item} value={item}>
            <Checkbox checked={items.map((t) => t.toString()).includes(item)} />
            <ListItemText primary={transformLabel ? transformLabel(item) : item} />
          </MenuItem>
        ))}
      </Select>
      <Collapse in={!!helperText}>
        <FormHelperText sx={{ fontSize: '12px' }}>{helperText ?? ' '}</FormHelperText>
      </Collapse>
    </FormControl>
  );
};
